import type { SkillDef } from "../types";
import { carriesOf, digits, fmt, placeParts, prob } from "../util";

/**
 * Digits per addend. Each rung adds one new demand: first no carrying, then carrying, then a longer number,
 * then a third addend. `carry`: false = no column carries, true = at least one, absent = either.
 */
const SPEC: Record<number, { d: number[]; carry?: boolean }> = {
  1: { d: [2, 1], carry: false }, 2: { d: [2, 2], carry: false }, 3: { d: [2, 2], carry: true },
  4: { d: [3, 2] }, 5: { d: [3, 3] }, 6: { d: [4, 3] }, 7: { d: [3, 3, 3] }, 8: { d: [4, 4] },
  9: { d: [4, 4, 4] }, 10: { d: [4, 4, 4, 4] },
};

export const add: SkillDef = {
  key: "add", name: "Addition", blurb: "up to four 4-digit numbers", start: 1,
  generate(L, r) {
    const { d: ds, carry } = SPEC[L];
    let nums: number[];
    do nums = ds.map((d) => r.int(10 ** (d - 1), 10 ** d - 1));
    while (carry !== undefined && (carriesOf(nums) > 0) !== carry);
    let total = nums[0];
    const lines: string[] = [];
    for (const n of nums.slice(1)) {
      const chain = [fmt(total)];
      for (const p of placeParts(n)) { total += p; chain.push(`+${fmt(p)} = ${fmt(total)}`); }
      lines.push(chain.join(", "));
    }
    const t = 1 + 0.5 * nums.reduce((a, n) => a + digits(n), 0) + 0.7 * carriesOf(nums) + (nums.length - 2);
    return prob(nums.map(fmt).join(" + "), total, "Left to right, one place at a time:\n" + lines.join("\n"), { t });
  },
};
